import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { TailorService } from '../tailor.service';
import {
  ArtifactKind,
  ConversationTurn,
  ProposalRef,
  RejectedOp,
  TailorTurnResponse
} from '../../interfaces/application-package';

// An agent turn renders a change summary and/or a free-text line. `note` is an
// independent field that may accompany changeCount (some ops applied, note
// explains what was not) OR stand alone (nothing applied but the request was
// understood). text/letterUpdated remain the seed/question/letter cases.
export interface ChatTurn {
  role: 'user' | 'agent';
  text?: string;
  note?: string;
  rejected?: RejectedOp[];
  changeCount?: number;
  letterUpdated?: boolean; // letter turn: regenerated whole letter, no diff to count
  edited?: boolean;         // this row is a human Edit turn marker (turn_type === 'edit')
  editChangeCount?: number;
  editedLetter?: boolean;   // the Edit turn is a letter edit (records no ops, so no count to show)
  proposal?: { id: number; summary: { op: string }[] }; // pending; renders Accept/Reject
  proposalResolved?: 'accepted' | 'rejected';           // after the user acts
  // Not yet wired: a stale proposal transitions straight to 'rejected' server-side,
  // and ConversationTurn does not currently carry a per-turn stale flag to render
  // a more specific marker for it. Kept as a documented, unused hook.
  proposalStale?: boolean;
  resolving?: boolean; // an accept/reject request is in flight — disables both buttons (F12)
}

@Component({
  selector: 'bst-tailor-chat',
  templateUrl: './tailor-chat.component.html',
  styleUrls: ['./tailor-chat.component.scss'],
  standalone: false
})
export class TailorChatComponent implements OnInit, OnDestroy {

  @Input() public packageId!: number;
  @Input() public turnCount: number = 1;
  @Input() public maxTurns: number = 25;
  @Input() public language: string = 'nl';
  /** Which artifact the refine instruction targets (workspace active tab). */
  @Input() public kind: ArtifactKind = 'cv';
  /** Set once the package is submitted/finalized and the consultant's edit
   * window is closed; disables the input and blocks the send shortcut. */
  @Input() public disabled = false;
  /** Prior turns, from the show() response; seeds `turns` on init so a reload
   * rehydrates the conversation instead of showing only the static seed line. */
  @Input() public conversation: ConversationTurn[] = [];
  /** The active artifact's still-open proposal (if any), from the show()
   * response; re-renders its Accept/Reject after a reload. */
  @Input() public pendingProposal: ProposalRef | null = null;

  @Output() public turnApplied = new EventEmitter<TailorTurnResponse>();
  /** Emitted when the user clicks the "N changes applied" chip — opens the Changes view. */
  @Output() public viewChanges = new EventEmitter<void>();
  /** Emitted from a pending proposal's review link — opens the Proposal view.
   *  Separate from viewChanges on purpose: the two views answer different
   *  questions and each link has to land on the one it names. */
  @Output() public viewProposal = new EventEmitter<void>();
  /** Mirrors `sending` so the parent can show a coverage-board loading state
      during a refine turn (the turn's response carries fresh coverage). Async so
      the ngOnDestroy reset (below) defers the parent write past the current
      change-detection pass, avoiding ExpressionChangedAfterItHasBeenChecked. */
  @Output() public busyChange = new EventEmitter<boolean>(true);

  @ViewChild('turnList') private turnListRef!: ElementRef<HTMLElement>;

  public turns: ChatTurn[] = [];
  public instruction = '';
  public sending = false;
  public showAdvanced = false;
  public unlockRoleTitle = false;
  public unlockSkillLabel = false;

  private subs: Subscription[] = [];

  constructor(
    private readonly tailor: TailorService,
    private readonly toastr: NbToastrService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.conversation?.length) {
      // The conversation is already kind-filtered by the parent, so every turn
      // here targets THIS instance's fixed `kind`.
      this.turns = this.conversation.map(t => {
        // A letter edit records no ops (the change is the full before/after
        // letter), so its change_count is always 0; say "edited the cover
        // letter" rather than "edited the CV (0 changes)".
        if (t.turn_type === 'edit') {
          return { role: 'user' as const, edited: true, editChangeCount: t.change_count, editedLetter: t.kind === 'letter' };
        }
        if (t.role === 'user') { return { role: 'user' as const, text: t.instruction ?? '' }; }
        if (t.turn_type === 'consult') { return { role: 'agent' as const, text: t.agent_text ?? '' }; }
        // Agent refine turn: a rejected/superseded proposal is NOT-APPLIED, so
        // render the resolved marker instead of a bogus "N changes applied" (F4).
        if (t.proposal_status === 'rejected') { return { role: 'agent' as const, proposalResolved: 'rejected' as const }; }
        // Accepted (or null): a letter refine rewrites the whole letter (no diff
        // to count) so it reads "Cover letter updated." (F8); a CV refine shows
        // its applied change count.
        return this.kind === 'letter'
          ? { role: 'agent' as const, letterUpdated: true }
          : { role: 'agent' as const, changeCount: t.change_count };
      });
    } else {
      this.turns = [{
        role: 'agent',
        text: this.translate.instant('cv.tailor.chat.seed')
          || 'Eerste versie is gegenereerd. Geef instructies om te verfijnen.'
      }];
    }
    // An unresolved proposal for the active artifact re-renders its Accept/Reject
    // after a reload (the conversation turns above only record that a refine
    // happened, not the still-open proposal).
    if (this.pendingProposal) {
      this.turns.push({
        role: 'agent',
        proposal: { id: this.pendingProposal.id, summary: this.pendingProposal.summary ?? [] },
      });
    }
  }

  ngOnDestroy(): void {
    // A mid-request teardown (switching to a not-yet-ready artifact tab, or
    // navigation) unsubscribes below without ever hitting a next/error handler,
    // so clear the busy signal here or the parent's coverage board would stay
    // stuck in its loading state.
    this.setSending(false);
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
  }

  /** Single write path for `sending` so every transition mirrors to the parent. */
  private setSending(value: boolean): void {
    if (this.sending === value) return;
    this.sending = value;
    this.busyChange.emit(value);
  }

  public get atLimit(): boolean {
    return this.turnCount >= this.maxTurns;
  }

  /**
   * Whether this bubble's proposal is still THE open one. `turns` is
   * append-only and nothing stops a second refine (or a direct workspace edit)
   * while a proposal is open, so an older bubble can outlive the proposal it
   * shows. Checked against the parent's pending id rather than mutated on
   * arrival, so a supersede from any source lands the same way.
   */
  public isOpen(turn: ChatTurn): boolean {
    return !!turn.proposal && turn.proposal.id === this.pendingProposal?.id;
  }

  public send(): void {
    if (this.atLimit || this.disabled) return;
    const instruction = this.instruction.trim();
    if (!instruction) return;

    this.turns.push({ role: 'user', text: instruction });
    this.setSending(true);

    const unlocked: string[] = [];
    if (this.unlockRoleTitle)  unlocked.push('set_experience_role_title');
    if (this.unlockSkillLabel) unlocked.push('set_skill_label');

    // Capture the target kind NOW: this.kind tracks the active tab and can change
    // if the user switches tabs while the request is in flight, which would
    // misclassify the response (Codex review).
    const kind = this.kind;

    const sub = this.tailor.refineTurn(this.packageId, {
      instruction,
      kind,
      unlocked: unlocked.length ? unlocked : undefined
    }).subscribe({
      next: resp => {
        if (resp.question) {
          this.setSending(false);
          // A clarifying question is a consult turn: the backend excludes it
          // from the refine budget, so it must NOT advance the round counter (F7).
          this.turns.push({ role: 'agent', text: resp.question });
          this.instruction = '';
          // Still emit so the parent refreshes the live quota chip: a consult
          // turn spends real tokens even though it doesn't consume a round (F11).
          this.turnApplied.emit(resp);
          this.scrollToBottom();
          return;
        }
        this.setSending(false);
        this.turnCount++;
        if (resp.proposal) {
          this.turns.push({
            role: 'agent',
            proposal: { id: resp.proposal.id, summary: resp.proposal.summary ?? [] },
            note: resp.note,
            rejected: resp.rejected?.length ? resp.rejected : undefined,
          });
        } else if (kind === 'letter') {
          // Defensive fallback: a letter refine now always returns a proposal,
          // so this only fires if the contract regresses.
          this.turns.push({ role: 'agent', letterUpdated: true });
        } else {
          // A consult/answer response (no proposal): render the agent's text.
          this.turns.push({ role: 'agent', text: resp.note ?? '' });
        }
        this.turnApplied.emit(resp);
        this.instruction = '';
        this.scrollToBottom();
      },
      error: (err: HttpErrorResponse) => {
        this.setSending(false);
        console.error(err);
        // A tagged code beats the generic "try again": letter_no_body means the
        // model returned nothing usable, so retrying the same words is the one
        // thing least likely to help (#1266).
        const code: string | undefined = err.error?.error;
        const coded = code ? this.translate.instant(`cv.tailor.chat.refineError.${code}`) : null;
        const msg = err.status === 429
          ? this.translate.instant('cv.errors.quotaReached')
          : ((coded && coded !== `cv.tailor.chat.refineError.${code}` ? coded : null)
            || err.error?.message
            || this.translate.instant('cv.tailor.chat.errorRefine')
            || 'Verfijnen mislukt. Probeer het opnieuw.');
        const ref = err.error?.correlation_id ? ` (ref: ${err.error.correlation_id})` : '';
        this.toastr.danger(
          msg + ref,
          this.translate.instant('cv.errors.title') || 'Fout'
        );
      }
    });
    this.subs.push(sub);
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.send();
    }
  }

  public acceptProposal(turn: ChatTurn): void {
    if (!turn.proposal || turn.resolving) return; // guard a double-fire (F12)
    turn.resolving = true;
    const sub = this.tailor.acceptProposal(this.packageId, turn.proposal.id).subscribe({
      next: resp => {
        turn.resolving = false;
        turn.proposalResolved = 'accepted';
        turn.proposal = undefined;
        this.turnApplied.emit(resp); // workspace re-reads the now-updated document
      },
      error: (err: HttpErrorResponse) => this.resolveError(turn, err, 'cv.tailor.chat.acceptFailed', 'Toepassen mislukt.'),
    });
    this.subs.push(sub);
  }

  public rejectProposal(turn: ChatTurn): void {
    if (!turn.proposal || turn.resolving) return; // guard a double-fire (F12)
    turn.resolving = true;
    const sub = this.tailor.rejectProposal(this.packageId, turn.proposal.id).subscribe({
      next: resp => {
        turn.resolving = false;
        turn.proposalResolved = 'rejected';
        turn.proposal = undefined;
        // reject now returns the show() envelope, so the parent restores the
        // live coverage + clears the pending proposal (F6 + F14).
        this.turnApplied.emit(resp);
      },
      error: (err: HttpErrorResponse) => this.resolveError(turn, err, 'cv.tailor.chat.rejectFailed', 'Afwijzen mislukt.'),
    });
    this.subs.push(sub);
  }

  /** Shared accept/reject error path: a 409 means this proposal was already
   *  resolved by something else -- a racing double-click, OR a set-aside
   *  toggle/redraft/template swap that superseded it server-side. The status
   *  code alone can't tell those apart, so the safe, truthful rendering is
   *  that this proposal did NOT apply, regardless of which action the caller
   *  intended (F12, corrected: a superseded proposal was previously shown as
   *  "accepted" on an accept-then-409, which told the user an AI change had
   *  applied when it had actually been discarded). Any other error surfaces
   *  the toast and leaves the buttons re-enabled to retry. */
  private resolveError(turn: ChatTurn, err: HttpErrorResponse, msgKey: string, fallback: string): void {
    turn.resolving = false;
    if (err.status === 409) {
      turn.proposalResolved = 'rejected';
      turn.proposal = undefined;
      this.toastr.info(
        this.translate.instant('cv.tailor.chat.proposalGone')
          || 'Dit voorstel is niet meer beschikbaar. Mogelijk is het vervangen door een nieuwere bewerking.',
        this.translate.instant('cv.errors.title') || 'Fout');
      return;
    }
    this.toastr.danger(
      this.translate.instant(msgKey) || fallback,
      this.translate.instant('cv.errors.title') || 'Fout');
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.turnListRef?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  }
}
